import React from "react";
import { useDispatch, useSelector } from "react-redux";
import secureLocalStorage from "react-secure-storage";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import {
  approveInsurance,
  deleteUnapprovedInsurance,
} from "../../actions/unApprovedInsurance";
import PageHeader from "../../ui/PageHeader";
import StatCard from "../../ui/StatCard";
import EmptyState from "../../ui/EmptyState";
import { brand } from "../../theme";
import { formatDate, formatMoney, moneyShort, sumBy } from "../../utils/premium";

function Detail({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" gap={2} sx={{ py: 0.75, borderBottom: `1px solid ${brand.border}` }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
        {value || "—"}
      </Typography>
    </Stack>
  );
}

export default function Approval() {
  const [approveOpen, setApproveOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [deleteText, setDeleteText] = React.useState("");

  const user = JSON.parse(secureLocalStorage.getItem("profile") || "null");
  const insurance = useSelector((state) => state.unApprovedInsurance) || [];
  const loaded = useSelector((state) => state.loaded.unApproved);
  const dispatch = useDispatch();

  const rows = insurance.map((d) => ({ ...d, id: d._id }));

  const openApproveModal = (row) => {
    setSelectedRow(row);
    setApproveOpen(true);
  };
  const closeApproveModal = () => {
    setApproveOpen(false);
    setSelectedRow(null);
  };
  const confirmApprove = () => {
    if (!selectedRow?.id) return;
    dispatch(
      approveInsurance(selectedRow.id, {
        approved: "true",
        approved_by:
          `${user?.results?.firstName || ""} ${user?.results?.lastName || ""}`.trim(),
      })
    );
    closeApproveModal();
  };

  const openDeleteModal = (row) => {
    setSelectedRow(row);
    setDeleteText("");
    setDeleteOpen(true);
  };
  const closeDeleteModal = () => {
    setDeleteOpen(false);
    setSelectedRow(null);
    setDeleteText("");
  };
  const confirmDelete = () => {
    if (!selectedRow?.id) return;
    dispatch(deleteUnapprovedInsurance(selectedRow.id));
    closeDeleteModal();
  };
  const canDelete = deleteText.trim().toLowerCase() === "delete";

  const columns = [
    { field: "policy_number", headerName: "Policy number", minWidth: 120, flex: 1 },
    { field: "description", headerName: "Description", minWidth: 130, flex: 1.3 },
    { field: "policy_type", headerName: "Policy type", minWidth: 100, flex: 0.9 },
    { field: "institution", headerName: "Institution", minWidth: 120, flex: 1.1 },
    { field: "cost", headerName: "Premium", type: "number", minWidth: 105, flex: 0.8, valueFormatter: ({ value }) => formatMoney(value) },
    { field: "purchase_date", headerName: "Start", minWidth: 100, valueFormatter: ({ value }) => formatDate(value) },
    { field: "maturity_date", headerName: "End", minWidth: 100, valueFormatter: ({ value }) => formatDate(value) },
    { field: "uploaded_by", headerName: "Entered by", minWidth: 110, flex: 0.9 },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      filterable: false,
      width: 215,
      align: "right",
      renderCell: ({ row }) => (
        <Stack direction="row" gap={1}>
          <Button size="small" variant="contained" color="secondary" startIcon={<CheckIcon />} onClick={() => openApproveModal(row)}>
            Approve
          </Button>
          <Button size="small" color="error" startIcon={<DeleteOutlineOutlinedIcon />} onClick={() => openDeleteModal(row)}>
            Delete
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Approvals"
        subtitle="New and renewed policies only count on the dashboard and report once approved."
      />

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard label="Waiting for approval" value={rows.length} icon={<PendingActionsOutlinedIcon />} color="#B45309" loading={!loaded} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard label="Premium waiting" value={moneyShort.format(sumBy(rows, "cost"))} icon={<PaymentsOutlinedIcon />} loading={!loaded} />
        </Grid>
      </Grid>

      <Box sx={{ height: "calc(100vh - 360px)", minHeight: 400 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={!loaded}
          density="compact"
          rowHeight={48}
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 100]}
          slots={{
            noRowsOverlay: () => (
              <EmptyState icon={<TaskAltOutlinedIcon fontSize="inherit" />} title="All caught up" text="No insurance is waiting for approval." />
            ),
          }}
        />
      </Box>

      <Dialog open={approveOpen} onClose={closeApproveModal} maxWidth="xs" fullWidth>
        <DialogTitle>Approve this insurance?</DialogTitle>
        <DialogContent>
          <Detail label="Policy number" value={selectedRow?.policy_number} />
          <Detail label="Institution" value={selectedRow?.institution} />
          <Detail label="Premium" value={formatMoney(selectedRow?.cost)} />
          <Detail label="Period" value={`${formatDate(selectedRow?.purchase_date)} – ${formatDate(selectedRow?.maturity_date)}`} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeApproveModal}>Cancel</Button>
          <Button onClick={confirmApprove} variant="contained" color="secondary" startIcon={<CheckIcon />}>
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={closeDeleteModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: "error.main" }}>Delete this insurance?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            This cannot be undone. Type <b>delete</b> to confirm.
          </Typography>
          <Detail label="Policy number" value={selectedRow?.policy_number} />
          <TextField
            autoFocus
            fullWidth
            sx={{ mt: 2 }}
            placeholder="delete"
            value={deleteText}
            onChange={(e) => setDeleteText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canDelete) confirmDelete();
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeDeleteModal}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" color="error" disabled={!canDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
